import React, { useMemo, useState } from 'react';
import { Box, Container, useColorModeValue } from '@chakra-ui/react';
import { Tabs } from 'src/components/molecules/DataBox/Tabs';
import { ConnectionsTab } from 'src/features/Settings/components/templates/ConnectionsTab';
import { PersonalTab } from 'src/features/Settings/components/templates/PersonalTab';
import { BillingTab } from 'src/features/Settings/components/templates/BillingTab';
import { CampaignsTab } from 'src/features/Settings/components/templates/CampaignsTab';
import { CampaignGroupsTab } from 'src/features/Settings/components/templates/CampaignGroupsTab';
import {
  ModelsTab,
  ModelsView,
} from 'src/features/Settings/components/templates/ModelsTab';
import { UserManagementTab } from 'src/features/Settings/components/templates/UserManagementTab';
import {
  CustomerRole,
  UserRole,
  useAuthContext,
} from 'src/contexts/AuthContext';
import { IS_DEV } from 'src/constants';

export type SettingsTab =
  | 'personal'
  | 'billing'
  | 'connections'
  | 'campaigns'
  | 'campaignGroups'
  | 'models'
  | 'userManagement';

export const SettingsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<SettingsTab>('personal');
  const [modelsView, setModelsView] = useState<ModelsView>({ type: 'list' });

  const { userProfile } = useAuthContext();
  const isServiceAdmin = userProfile?.role === UserRole.ADMIN;
  const isCustomerAdmin = userProfile?.customerRole === CustomerRole.ADMIN;
  const canManageUsers = isServiceAdmin || isCustomerAdmin;

  const bgColor = 'surface.panel';
  const textColor = useColorModeValue('gray.800', 'white');

  const tabOptions = useMemo(
    () => [
      { label: 'Personal', value: 'personal' as SettingsTab },
      ...(IS_DEV
        ? [{ label: 'Billing', value: 'billing' as SettingsTab }]
        : []),
      { label: 'Connections', value: 'connections' as SettingsTab },
      { label: 'Campaigns', value: 'campaigns' as SettingsTab },
      { label: 'Campaign Groups', value: 'campaignGroups' as SettingsTab },
      { label: 'Models', value: 'models' as SettingsTab },
      ...(canManageUsers
        ? [{ label: 'User Management', value: 'userManagement' as SettingsTab }]
        : []),
    ],
    [canManageUsers],
  );

  const handleTabSelect = (tab: SettingsTab) => {
    setSelectedTab(tab);
    if (tab === 'models') {
      setModelsView({ type: 'list' });
    }
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'personal':
        return <PersonalTab />;
      case 'billing':
        return <BillingTab />;
      case 'connections':
        return <ConnectionsTab />;
      case 'campaigns':
        return <CampaignsTab />;
      case 'campaignGroups':
        return <CampaignGroupsTab />;
      case 'models':
        return <ModelsTab view={modelsView} onViewChange={setModelsView} />;
      case 'userManagement':
        return <UserManagementTab />;
      default:
        return <PersonalTab />;
    }
  };

  return (
    <Box bg={bgColor} minH="70vh" color={textColor} w="100%">
      <Container maxW="100%" px={10} py={8}>
        {/* Tab Navigation */}
        <Box
          mb={8}
          overflowX="auto"
          css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
          <Tabs
            options={tabOptions}
            onSelect={handleTabSelect}
            selectedTab={selectedTab}
          />
        </Box>

        {/* Tab Content */}
        <Box maxW="100%">{renderTabContent()}</Box>
      </Container>
    </Box>
  );
};
